import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import MuscleHeadTextIcon from "../../assets/MuscleHeadTextIcon";

import LanguageSelect from "../shared-theme/components/LanguageSelect";
import { getOurSupportEmail } from "@config/config"

import { useConfirmRegistrationMutation } from "@mutation/authMutation";
import AppTheme from "../shared-theme/AppTheme";

const Card = styled(MuiCard)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    width: "100%",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: "auto",
    [theme.breakpoints.up("sm")]: {
      maxWidth: "450px",
    },
    boxShadow:
      "hsla(350, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.05) 0px 15px 35px -5px",
    ...theme.applyStyles("dark", {
      boxShadow:
        "hsla(350, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.08) 0px 15px 35px -5px",
    }),
  }));
  
  const SignInContainer = styled(Stack)(({ theme }) => ({
    minHeight: "100vh",
    padding: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(4),
    },
    position: "relative",
    "&::before": {
      content: '""',
      display: "block",
      position: "absolute",
      zIndex: -1,
      inset: 0,
      backgroundImage:
        "radial-gradient(ellipse at 50% 50%, hsl(340, 100%, 97%), hsl(0, 0%, 100%))",
      backgroundRepeat: "no-repeat",
      ...theme.applyStyles("dark", {
        backgroundImage:
          "radial-gradient(at 50% 50%, hsla(340, 100%, 16%, 0.5), hsla(350, 30%, 5%))",
      }),
    },
  }));

export default function ConfirmEmail(props) {
    const [code, setCode] = useState("");
    const [codeError, setCodeError] = useState(false);
    const [codeErrorMessage, setCodeErrorMessage] = useState("");
    const { t } = useTranslation("auth");
    const navigate = useNavigate();
    const confirmRegistrationMutation = useConfirmRegistrationMutation();
  
    const handleSubmit = async (event) => {
      event.preventDefault();
      if (!validateInputs()) return;
  
      confirmRegistrationMutation.mutate(
        { data: {code:code} },
        {
          onSuccess: (response) => {
            if (response.data) // return true or false
                navigate(`/signin`);
            else {
                setCodeError(true);
                setCodeErrorMessage(t("invalidCode"));
            }
          },
          onError: () => {
            setCodeError(true);
            setCodeErrorMessage(t("invalidCode"));
          },
        }
      );
    };
  
    const validateInputs = () => {
      if (code.length !== 6 || isNaN(code)) {
        setCodeError(true);
        setCodeErrorMessage(t("validationErrorCode"));
        return false;
      }
      setCodeError(false);
      setCodeErrorMessage("");
      return true;
    };
  
    return (
        <AppTheme {...props}>
              <CssBaseline enableColorScheme />
              <SignInContainer direction="column" justifyContent="space-between">
                
                <Box
                  sx={{
                    position: "fixed",
                    top: "1rem",
                    right: "1rem",
                    display: "flex",
                    gap: "0.5rem",
                  }}
                >
                  <LanguageSelect />
                </Box>
                <Card variant="outlined">
                  <MuscleHeadTextIcon
                    sx={{
                      alignSelf: "center",
                      height: "160px",
                      width: "100%",
                      marginBottom: "40px",
                    }}
                  />
                  <Typography
                    component="h1"
                    variant="h4"
                    sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
                  >
                    {t("confirmEmail")}
                  </Typography>
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    <FormControl>
                      <FormLabel htmlFor="code">{t("code")}</FormLabel>
                      <TextField
                  error={codeError}
                  helperText={codeErrorMessage}
                  id="code"
                  type="text"
                  name="code"
                  inputProps={{ maxLength: 6, pattern: "[0-9]*", inputMode: "numeric" }}
                  required
                  fullWidth
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                /> 
                    </FormControl>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      // onClick={validateInputs}
                      sx={{ marginTop: 4
                       }}
                    >
                      {t("confirm", { context: "button" })}
                    </Button>
                  </Box>
                  <Button
                    component={RouterLink} to="/signin"
                    fullWidth
                    variant="contained"
                    >
                    {t("backToLogin")}
                  </Button>
        
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    href={"mailto:" + getOurSupportEmail()}
                    sx={{ alignSelf: "center", color: "gray" }} // TODO: link the color with the theme
                  >
                    {t("contactUs")}
                  </Link>
                </Card>
              </SignInContainer>
            </AppTheme>
    );
  }